/**
 * Was der **Text** kennt, als TipTap-Schema (ADR 0029) — geteilt vom
 * Schreibfeld (`CanvasTextEditor`) und vom kopflosen Formatieren des ganzen
 * Texts (`canvasTextFormat.ts`).
 *
 * **Eigene Marks statt TipTaps Highlight/TextStyle:** Deren HTML trüge die
 * Werte als `style="…"`; gespeichert werden aber nur Schlüssel
 * (`<mark data-color>`, `<span data-color data-font data-size>`). Sichtbar
 * werden die Werte im Schreibfeld über Dekorationen (`CanvasTextLook`), die
 * nicht in `getHTML()` landen.
 *
 * Was das Schema nicht kennt, fällt beim Einfügen von selbst weg — Links werden
 * Text, Überschriften Absätze.
 */

import { Extension, Mark, type Extensions } from '@tiptap/core'
import type { Mark as PmMark, Node as PmNode } from '@tiptap/pm/model'
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Underline from '@tiptap/extension-underline'
import Strike from '@tiptap/extension-strike'
import { BulletList, ListItem, ListKeymap } from '@tiptap/extension-list'
import { clampCanvasTextSize, DEFAULT_CANVAS_TEXT_HIGHLIGHT } from './canvasText'
import { canvasTextMarkerClass, canvasTextMarkerData, canvasTextMarkerLook, canvasTextMarkerRuns, canvasTextMarkerVars, canvasTextShapedMarkerClass, randomCanvasTextMarkerLook, shuffledCanvasTextMarkerLook } from './canvasTextMarker'
import {
  CANVAS_TEXT_PALETTE,
  canvasTextInlineStyle,
  type CanvasTextColorPalette,
  isCanvasTextFontKey,
  isCanvasTextHighlightKey,
  parseCanvasTextMarkerLook,
  type CanvasTextMarks,
} from './canvasTextContent'

/** Was an einer Stelle vom Grundwert des Texts abweichen kann. */
export type CanvasTextStyleAttr = 'color' | 'font' | 'size'

type StyleAttrs = { color: string | null; font: string | null; size: number | null }

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    canvasTextStyle: {
      /** Setzt einen Wert auf der Markierung; `null` heißt „wie der Grundwert". */
      setCanvasTextStyle: (attr: CanvasTextStyleAttr, value: string | number | null) => ReturnType
      /** Räumt einen Wert aus dem ganzen Text — der Grundwert gilt wieder überall. */
      clearCanvasTextStyle: (attr: CanvasTextStyleAttr) => ReturnType
    }
    canvasHighlight: {
      /**
       * Markiert die Markierung in dieser Farbe; jeder Markerstrich darin
       * bekommt seine eigene Formnummer. Ohne Markierung: das nächste Getippte.
       *
       * Mit `look` wird nur UMGEFÄRBT: Was schon einen Marker trägt, behält
       * seine Form, der Rest bekommt `look`. So wechselt die Form nicht bei
       * jedem Tropfen, den die Vorschau überfährt — neu gemischt wird allein
       * über „Marker wechseln" (gemeldet 2026-09-24).
       */
      setCanvasHighlight: (color: string, look?: number) => ReturnType
      /** „Marker wechseln": jeder Markerstrich im ganzen Text bekommt eine neue Formnummer. */
      shuffleCanvasMarkers: () => ReturnType
    }
  }
}

/** Der Textmarker: `<mark data-color="green">`. */
const CanvasHighlight = Mark.create({
  name: 'canvasHighlight',
  addAttributes() {
    return {
      color: {
        default: DEFAULT_CANVAS_TEXT_HIGHLIGHT,
        // Ohne bekannte Farbe bleibt ein Marker ein Marker — gelb (wie `parseCanvasText`).
        parseHTML: element => {
          const color = element.getAttribute('data-color')
          return isCanvasTextHighlightKey(color) ? color : DEFAULT_CANVAS_TEXT_HIGHLIGHT
        },
        renderHTML: attributes => ({ 'data-color': attributes.color }),
      },
      // Die Formnummer (`canvasTextMarker.ts`) — ein Schlüssel wie die Farbe,
      // kein Stil (ADR 0029). Fehlt sie, folgt die Form aus der Lage im Text.
      look: {
        default: null,
        parseHTML: element => parseCanvasTextMarkerLook(element.getAttribute('data-look')),
        renderHTML: attributes => (attributes.look === null ? {} : { 'data-look': String(attributes.look) }),
      },
    }
  },
  parseHTML() {
    return [{ tag: 'mark' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['mark', HTMLAttributes, 0]
  },
  addCommands() {
    return {
      setCanvasHighlight: (color, look) => ({ tr, state, dispatch }) => {
        if (!dispatch) return true
        const type = state.schema.marks.canvasHighlight!
        const { from, to, empty } = state.selection
        if (empty) {
          tr.addStoredMark(type.create({ color, look: look ?? randomCanvasTextMarkerLook() }))
          return true
        }
        if (look !== undefined) {
          recolorMarkers(tr, from, to, color, look)
          return true
        }
        tr.addMark(from, to, type.create({ color, look: null }))
        assignMarkerLooks(tr, from, to)
        return true
      },
      shuffleCanvasMarkers: () => ({ tr, dispatch }) => {
        if (dispatch) assignMarkerLooks(tr, 0, tr.doc.content.size)
        return true
      },
    }
  },
})

/**
 * Die Markerstriche eines Dokuments: je Zeile die zusammenhängenden Läufe mit
 * derselben Farbe und Formnummer — dieselbe Einteilung wie beim Zeichnen
 * (`canvasTextMarkerRuns`).
 */
type MarkerStroke = { from: number; to: number; color: string; look: number | null; pieces: number }

function markerStrokesOf(doc: PmNode): MarkerStroke[] {
  const strokes: MarkerStroke[] = []
  doc.descendants((block, blockPos) => {
    if (!block.isTextblock) return true
    let current: MarkerStroke | null = null
    block.forEach((child, offset) => {
      const mark = child.isText ? child.marks.find(m => m.type.name === 'canvasHighlight') : undefined
      const pos = blockPos + 1 + offset
      if (mark && current && current.color === mark.attrs.color && current.look === mark.attrs.look && current.to === pos) {
        current.to = pos + child.nodeSize
        current.pieces += 1
        return
      }
      current = mark ? { from: pos, to: pos + child.nodeSize, color: mark.attrs.color, look: mark.attrs.look, pieces: 1 } : null
      if (current) strokes.push(current)
    })
    return false
  })
  return strokes
}

/** Färbt die Strecke um; was schon einen Marker trägt, behält seine Formnummer, der Rest bekommt `look`. */
function recolorMarkers(tr: Transaction, from: number, to: number, color: string, look: number): void {
  const type = tr.doc.type.schema.marks.canvasHighlight!
  tr.doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText) return true
    const start = Math.max(pos, from)
    const end = Math.min(pos + node.nodeSize, to)
    const own = type.isInSet(node.marks)?.attrs.look as number | null | undefined
    tr.addMark(start, end, type.create({ color, look: own ?? look }))
    return false
  })
}

/** Jeder Markerstrich, der die Strecke berührt, bekommt eine neue Formnummer. */
function assignMarkerLooks(tr: Transaction, from: number, to: number): void {
  const type = tr.doc.type.schema.marks.canvasHighlight!
  for (const stroke of markerStrokesOf(tr.doc)) {
    if (stroke.to <= from || stroke.from >= to) continue
    tr.addMark(stroke.from, stroke.to, type.create({ color: stroke.color, look: shuffledCanvasTextMarkerLook(stroke.look, stroke.pieces) }))
  }
}

/** Farbe, Schrift und Größe einer Stelle: `<span data-color data-font data-size>`. */
const CanvasTextStyle = Mark.create<{ palette: CanvasTextColorPalette }>({
  name: 'canvasTextStyle',
  addOptions() {
    return { palette: CANVAS_TEXT_PALETTE }
  },
  addAttributes() {
    const { palette } = this.options
    return {
      color: {
        default: null,
        parseHTML: element => {
          const value = element.getAttribute('data-color')
          return value && palette.isColorKey(value) ? value : null
        },
        renderHTML: attributes => (attributes.color ? { 'data-color': attributes.color } : {}),
      },
      font: {
        default: null,
        parseHTML: element => {
          const value = element.getAttribute('data-font')
          return isCanvasTextFontKey(value) ? value : null
        },
        renderHTML: attributes => (attributes.font ? { 'data-font': attributes.font } : {}),
      },
      size: {
        default: null,
        parseHTML: element => {
          const size = Number(element.getAttribute('data-size'))
          return element.hasAttribute('data-size') && Number.isFinite(size) && size > 0 ? clampCanvasTextSize(size) : null
        },
        renderHTML: attributes => (attributes.size ? { 'data-size': String(attributes.size) } : {}),
      },
    }
  },
  parseHTML() {
    return [{
      tag: 'span',
      // Ein `<span>` ohne einen der drei Werte ist nichts, was der Text kennt.
      getAttrs: element => (['data-color', 'data-font', 'data-size'].some(name => element.hasAttribute(name)) ? null : false),
    }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0]
  },
  addCommands() {
    return {
      setCanvasTextStyle: (attr, value) => ({ tr, state, dispatch }) => {
        if (!dispatch) return true
        const { from, to, empty } = state.selection
        const type = state.schema.marks.canvasTextStyle!
        if (empty) {
          const current = type.isInSet(state.storedMarks ?? state.selection.$from.marks())
          const attrs = { ...emptyStyle(), ...current?.attrs, [attr]: value } as StyleAttrs
          tr.removeStoredMark(type)
          if (hasStyle(attrs)) tr.addStoredMark(type.create(attrs))
          return true
        }
        setStyleInRange(tr, from, to, attr, value)
        return true
      },
      clearCanvasTextStyle: attr => ({ tr, dispatch }) => {
        if (dispatch) setStyleInRange(tr, 0, tr.doc.content.size, attr, null)
        return true
      },
    }
  },
})

function emptyStyle(): StyleAttrs {
  return { color: null, font: null, size: null }
}

function hasStyle(attrs: StyleAttrs): boolean {
  return attrs.color !== null || attrs.font !== null || attrs.size !== null
}

/**
 * Einen Wert der Stil-Mark über eine Strecke setzen, die übrigen Werte jeder
 * Stelle bleiben. Mark-Schritte verschieben keine Positionen — über den Stand
 * VOR den Schritten zu laufen ist deshalb sicher.
 */
export function setStyleInRange(
  tr: Transaction,
  from: number,
  to: number,
  attr: CanvasTextStyleAttr,
  value: string | number | null,
): void {
  const type = tr.doc.type.schema.marks.canvasTextStyle!
  const doc = tr.doc
  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText) return
    const start = Math.max(pos, from)
    const end = Math.min(pos + node.nodeSize, to)
    const current = type.isInSet(node.marks)
    if (!current && value === null) return
    const attrs = { ...emptyStyle(), ...current?.attrs, [attr]: value } as StyleAttrs
    tr.removeMark(start, end, type)
    if (hasStyle(attrs)) tr.addMark(start, end, type.create(attrs))
  })
}

/** Jede Größe im Text mal `factor` — die Ecken skalieren Abweichungen mit. */
export function scaleSizesInDoc(tr: Transaction, factor: number): void {
  const type = tr.doc.type.schema.marks.canvasTextStyle!
  tr.doc.descendants((node, pos) => {
    if (!node.isText) return
    const current = type.isInSet(node.marks)
    if (!current?.attrs.size) return
    const size = clampCanvasTextSize(Math.round(current.attrs.size * factor))
    tr.removeMark(pos, pos + node.nodeSize, type)
    tr.addMark(pos, pos + node.nodeSize, type.create({ ...current.attrs, size }))
  })
}

/** Die Marks einer Stelle in der Form, die auch `parseCanvasText` liefert. */
export function canvasTextMarksOf(marks: readonly PmMark[]): CanvasTextMarks {
  const out: CanvasTextMarks = {}
  for (const mark of marks) {
    switch (mark.type.name) {
      case 'bold': out.bold = true; break
      case 'italic': out.italic = true; break
      case 'underline': out.underline = true; break
      case 'strike': out.strike = true; break
      case 'canvasHighlight':
        out.highlight = mark.attrs.color
        if (mark.attrs.look !== null) out.look = mark.attrs.look
        break
      case 'canvasTextStyle':
        if (mark.attrs.color) out.color = mark.attrs.color
        if (mark.attrs.font) out.font = mark.attrs.font
        if (mark.attrs.size) out.size = mark.attrs.size
        break
    }
  }
  return out
}

const kebab = (name: string) => name.replace(/[A-Z]/g, c => `-${c.toLowerCase()}`)

/**
 * Die Stile und die **Markerstriche** des Markers als Dekorationen — Zeile für Zeile
 * und in derselben Reihenfolge gezählt wie `CanvasTextContent`, damit ein
 * Marker beim Schreiben dieselbe Form hat wie in Ruhe.
 */
function lookOf(doc: PmNode, seed: string, palette: CanvasTextColorPalette): DecorationSet {
  const decorations: Decoration[] = []
  let nextStroke = 0
  doc.descendants((block, blockPos) => {
    if (!block.isTextblock) return true
    const texts: { node: PmNode; pos: number }[] = []
    block.forEach((child, offset) => { if (child.isText) texts.push({ node: child, pos: blockPos + 1 + offset }) })
    const marks = texts.map(({ node }) => canvasTextMarksOf(node.marks))
    const { pieces, next } = canvasTextMarkerRuns(marks.map(m => m.highlight ?? null), nextStroke, marks.map(m => m.look))
    nextStroke = next
    texts.forEach(({ node, pos }, index) => {
      const style: Record<string, string | number> = { ...canvasTextInlineStyle(marks[index]!, palette) }
      const piece = pieces[index]
      const look = piece ? canvasTextMarkerLook(seed, piece) : null
      if (piece && look) Object.assign(style, canvasTextMarkerVars(piece, look, node.text ?? '', palette.markerShape))
      const css = Object.entries(style).map(([key, value]) => `${kebab(key)}: ${value}`).join('; ')
      /*
        Unterstreichen und Durchstreichen zieht das Stück selbst, nicht das
        `<u>`/`<s>` darum: Die Farbe sitzt an diesem Stück, und die Linie soll
        in ihr stehen (gemeldet 2026-09-26). `<u>`/`<s>` zeichnen im Feld nichts.
      */
      const lineClass = [
        marks[index]!.underline && 'text-node__mark--underline',
        marks[index]!.strike && 'text-node__mark--strike',
      ].filter(Boolean).join(' ')
      const markerClass = piece && look
        ? (palette.markerShape ? canvasTextShapedMarkerClass(piece) : canvasTextMarkerClass(piece, look))
        : ''
      const className = [markerClass, lineClass].filter(Boolean).join(' ')
      if (css || className) {
        decorations.push(Decoration.inline(pos, pos + node.nodeSize, {
          ...(css ? { style: css } : {}),
          ...(className ? { class: className } : {}),
          ...(piece && look && palette.markerShape ? canvasTextMarkerData(piece, look) : {}),
        }))
      }
    })
    return false
  })
  return DecorationSet.create(doc, decorations)
}

const lookKey = new PluginKey<DecorationSet>('canvasTextLook')

/** Macht die gespeicherten Schlüssel im Schreibfeld sichtbar — wie `CanvasTextContent` in Ruhe. */
const CanvasTextLook = Extension.create<{ seed: string; palette: CanvasTextColorPalette }>({
  name: 'canvasTextLook',
  addOptions() {
    return { seed: '', palette: CANVAS_TEXT_PALETTE }
  },
  addProseMirrorPlugins() {
    const { seed, palette } = this.options
    return [new Plugin<DecorationSet>({
      key: lookKey,
      state: {
        init: (_, state) => lookOf(state.doc, seed, palette),
        apply: (tr, current) => (tr.docChanged ? lookOf(tr.doc, seed, palette) : current),
      },
      props: {
        decorations: state => lookKey.getState(state),
      },
    })]
  },
})

/**
 * Ein Listeneintrag ist genau ein Absatz: Verschachteln (⇥) kennt der Text
 * nicht, und ⇥ verlässt das Feld wie überall auf dem Canvas.
 */
const CanvasListItem = ListItem.extend({
  content: 'paragraph',
  addKeyboardShortcuts() {
    return { Enter: () => this.editor.commands.splitListItem(this.name) }
  },
})

/**
 * Das Schema des Texts — ohne Verlauf (den bringt nur das Schreibfeld mit).
 * `seed` ist die Kennung des Texts, aus der die Form seiner Marker folgt.
 *
 * Der **Projekttitel** nimmt dasselbe Schema (CONTEXT.md „Projekt-Kopf“) —
 * mit eigener Farbpalette, ohne Listen und mit genau einem Absatz.
 */
export function canvasTextExtensions({
  seed = '',
  palette = CANVAS_TEXT_PALETTE,
  singleLine = false,
}: { seed?: string; palette?: CanvasTextColorPalette; singleLine?: boolean } = {}): Extensions {
  return [
    singleLine ? SingleParagraphDocument : Document,
    Paragraph,
    Text,
    Bold,
    Italic,
    Underline,
    Strike,
    CanvasHighlight,
    CanvasTextStyle.configure({ palette }),
    ...(singleLine ? [] : [BulletList, CanvasListItem, ListKeymap]),
    CanvasTextLook.configure({ seed, palette }),
  ]
}

/** Ein Dokument aus genau einem Absatz — eine Überschrift hat keine zweite Zeile. */
const SingleParagraphDocument = Document.extend({ content: 'paragraph' })

/** Klartext zeilenweise in Absätze — für eine Zeile, die noch kein HTML trägt. */
export function toCanvasTextHtml(content: string): string {
  if (/^\s*</.test(content)) return content
  const escape = (line: string) => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return content.split('\n').map(line => `<p>${escape(line)}</p>`).join('')
}
